import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus, MessageSquare, CheckCircle2,
  Pencil, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Printer, FileSpreadsheet, Filter, CalendarDays, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/api";
import { normalizeManagementType } from "@/lib/notificationRouting";
import { AREA_OPTIONS, getAreaLabel } from "@/lib/areaOptions";
import { usePagination } from "@/lib/usePagination";
import { OperatorMultiSelect } from "@/components/ui/operator-multi-select";
import HistoryPanel from "@/components/HistoryPanel";
import ClientCombobox from "@/components/ClientCombobox";
import SafeModeLavoroQuotidiano from "./SafeModeLavoroQuotidiano";
import PraticheQuotidianeTabRebuilt from "./PraticheQuotidianeTabRebuilt";

const MGMT_OPTIONS = [
  { value: "agenzia", label: "Agenzia" },
  { value: "studio", label: "Studio" },
];

// Mesi italiani — usati dai filtri Anno/Mese (refinement 22/02/2026).
const MONTHS_IT = [
  { value: "01", label: "Gennaio" },
  { value: "02", label: "Febbraio" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Aprile" },
  { value: "05", label: "Maggio" },
  { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" },
];
const monthLabel = (m) => MONTHS_IT.find((x) => x.value === m)?.label || m;
// Ultimo giorno del mese (1-12) di un dato anno, formato "DD".
const lastDayOfMonth = (year, monthNum) => {
  const d = new Date(Number(year), Number(monthNum), 0).getDate();
  return String(d).padStart(2, "0");
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const nowHHMM = () =>
  new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

// "Lavoro Quotidiano" è un registro operativo di lavori SVOLTI / da
// registrare, non uno scadenzario. Quindi la visual class fa solo
// "chiusa = verde tenue" e nessuna logica overdue/in-ritardo: la data
// del lavoro è la data di esecuzione/registrazione, non una deadline.
function rowVisualClass(p) {
  if (p.status === "chiusa") return "bg-green-50/60 hover:bg-green-50";
  return "hover:bg-slate-50";
}

function emptyForm(currentOperator) {
  return {
    clientId: "",
    clientName: "",
    managementType: "agenzia",
    operationalArea: "Lavoro",
    creatorOperatorId: currentOperator?.id || "",
    creatorOperatorName: currentOperator?.fullName || currentOperator?.name || "",
    // Backend field `expectedDueDate` riusato semanticamente come
    // "Data lavoro" (data di esecuzione/registrazione del lavoro
    // svolto). Default a oggi così l'operatore non perde tempo a
    // selezionare ogni volta la data corrente.
    expectedDueDate: todayISO(),
    // 19/02/2026 — orario di registrazione (default = ora corrente).
    // Coerente con il pattern Diario (contactDate + contactTime).
    expectedDueTime: nowHHMM(),
    description: "",
    expenses: { amount: 0, paid: false, description: "", paymentMethod: "" },
    status: "aperta",
    // Operational simplification (18/02/2026). By default the work
    // is treated as ALREADY DONE → backend auto-closes it on save.
    // Tick "Lavoro ancora aperto" to keep the full lifecycle
    // (status=aperta, overdue scan, quick notes, manual close).
    isOpen: false,
    // Assignment fields (20/02/2026). Both kept consistent at save time:
    // `assignedOperatorId` carries the FIRST item of `assignedOperatorIds`
    // for backward compatibility.
    assignedOperatorId: "",
    assignedOperatorIds: [],
    assignedOperatorName: "",
  };
}

// Sorting helpers (richiesta #7).
const COMPARATORS = {
  clientName: (a, b) => (a.clientName || "").localeCompare(b.clientName || "", "it"),
  // Default: ordina per data lavoro decrescente (i lavori più recenti in
  // cima). Il client-side flip su `asc/desc` gestisce l'inversione.
  expectedDueDate: (a, b) => (a.expectedDueDate || "0000-00-00").localeCompare(b.expectedDueDate || "0000-00-00"),
  creatorOperatorName: (a, b) =>
    (a.creatorOperatorName || "").localeCompare(b.creatorOperatorName || "", "it"),
  status: (a, b) => (a.status || "").localeCompare(b.status || ""),
};

/**
 * Piccolo helper locale: un Select stilato in modo coerente con i filtri
 * del Diario Giornaliero (label-sopra-trigger). Differisce da
 * `LabeledFilterSelect` (che ha la label baked-in nel trigger come
 * "Operatore: Tutti") perché qui la label sta SOPRA in stile uppercase,
 * mantenendo identica visual hierarchy in entrambi i moduli operativi.
 *
 * Gestisce il caso `value=""` ↔ Radix `__all__` come l'altro componente.
 */
function LabeledFilterSelectInline({ value, onChange, options, testId, placeholder = "Tutti" }) {
  const ALL = "__all__";
  const radixValue = value === "" || value == null ? ALL : value;
  return (
    <Select value={radixValue} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="h-9 text-sm w-full" data-testid={testId}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Isolation stages (22/02/2026 — directive utente diagnostico).
//
// **DEFAULT (no query param) = versione SAFE in sola lettura,
// stabile e a prova di crash.**
// Per la versione completa con dialog/azioni → `?stage=full`
// (avviso: può crashare nel browser dell'utente, è la versione
// originale invariata mentre identifichiamo il blocco rotto).
//
// Diagnostica granulare: `?stage=N` (0-10) isola blocchi specifici:
//   stage=0  → shell vuoto (solo <div>)
//   stage=1  → + intestazione
//   stage=2  → + data fetch da API (lista grezza, solo conteggio)
//   stage=3  → + filtri Anno/Mese con <select> NATIVO (no Radix)
//   stage=4  → + lista <ul><li> piana (no <table>) — id db come key
//   stage=5  → + struttura <table> HTML nativa con header + tbody
//   stage=6  → + Card wrapper (shadcn)
//   stage=7  → + Radix Select per filtri Anno/Mese (rimpiazza i
//              <select> nativi)
//   stage=8  → + pagination via usePagination
//   stage=9  → + PracticeTable (componente memoizzato con multi-tbody)
//   stage=10 → + Dialog (Note + Close) + action buttons + expanded rows
//   stage=full → versione completa originale (potenzialmente broken).
//
// Default rendering (no `?stage`): `SafeModeLavoroQuotidiano` da
// `./SafeModeLavoroQuotidiano.jsx` — read-only, no Radix, no portal.
// ──────────────────────────────────────────────────────────────────────
function readStageFromURL() {
  if (typeof window === "undefined") return "default";
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("stage");
    if (raw === null) return "default";
    if (raw === "full" || raw === "rebuilt") return "rebuilt"; // new default rebuilt
    if (raw === "legacy") return "legacy"; // OLD broken full (emergency)
    if (raw === "readonly" || raw === "safe") return "readonly";
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0 && n <= 10) return n;
    return "default";
  } catch { return "default"; }
}

export default function PraticheQuotidianeTab(props) {
  const stage = readStageFromURL();
  if (stage === "legacy") {
    // Versione vecchia (crashava). Mantenuta SOLO per emergenza/debug.
    return <PraticheQuotidianeTabFull {...props} />;
  }
  if (stage === "readonly") {
    // Sola lettura senza azioni (SafeMode puro).
    return <PraticheQuotidianeTabSafeDefault />;
  }
  if (typeof stage === "number") {
    return <PraticheQuotidianeTabIsolated stage={stage} />;
  }
  // Default e `?stage=full|rebuilt` → versione RICOSTRUITA stabile
  // (no Radix Select, no Radix Dialog, modali inline, native HTML).
  return <PraticheQuotidianeTabRebuilt {...props} />;
}

function PraticheQuotidianeTabSafeDefault() {
  const goFull = () => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("stage", "full");
    window.location.href = url.toString();
  };
  return (
    <div data-testid="lavoro-quotidiano-tab">
      <div style={{ background: "#e0f2fe", border: "1px solid #38bdf8", padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
        <strong>Versione stabile in sola lettura.</strong> Modulistica + creazione/modifica voci sono temporaneamente disponibili solo nella versione completa.
        <button
          type="button"
          onClick={goFull}
          style={{ marginLeft: 12, padding: "4px 10px", background: "#0284c7", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
          data-testid="pq-go-full-btn"
        >
          Apri versione completa (?stage=full)
        </button>
      </div>
      <SafeModeLavoroQuotidiano />
    </div>
  );
}

function PraticheQuotidianeTabIsolated({ stage }) {
  // ── Stage 0: solo shell ─────────────────────────────────────────────
  if (stage === 0) {
    return (
      <div data-testid="lavoro-quotidiano-tab" style={{ padding: 16, border: "1px dashed #cbd5e1", borderRadius: 4 }}>
        <strong>Lavoro Quotidiano — STAGE 0 (shell)</strong>
        <p style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
          Solo div statico. Nessun useState, nessun useEffect, nessuna
          libreria esterna, nessun fetch.
        </p>
      </div>
    );
  }
  return <PraticheQuotidianeTabStaged stage={stage} />;
}

function PraticheQuotidianeTabStaged({ stage }) {
  // Hooks usati progressivamente dagli stage 1+. Tutti dichiarati qui
  // così l'ordine degli hook è stabile tra render.
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(stage >= 2);
  const [filterYear, setFilterYear] = React.useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = React.useState("all");
  const [filterMonthTo, setFilterMonthTo] = React.useState("all");

  React.useEffect(() => {
    if (stage < 2) return;
    let alive = true;
    api.listDailyPractices()
      .then((data) => {
        if (!alive) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setItems(list);
        setLoading(false);
      })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [stage]);

  const MONTHS_IT_LOCAL = [
    ["01", "Gennaio"], ["02", "Febbraio"], ["03", "Marzo"], ["04", "Aprile"],
    ["05", "Maggio"], ["06", "Giugno"], ["07", "Luglio"], ["08", "Agosto"],
    ["09", "Settembre"], ["10", "Ottobre"], ["11", "Novembre"], ["12", "Dicembre"],
  ];
  const lastDay = (y, m) => String(new Date(Number(y), Number(m), 0).getDate()).padStart(2, "0");

  const filtered = React.useMemo(() => {
    if (stage < 3 || filterYear === "all") return items;
    const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
    const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
    let m1 = mFrom, m2 = mToRaw || mFrom;
    if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
    const from = m1 ? `${filterYear}-${m1}-01` : `${filterYear}-01-01`;
    const to = m2 ? `${filterYear}-${m2}-${lastDay(filterYear, m2)}` : `${filterYear}-12-31`;
    return items.filter(p => {
      const d = (p.expectedDueDate || "").slice(0, 10);
      return d && d >= from && d <= to;
    });
  }, [items, filterYear, filterMonthFrom, filterMonthTo, stage]);

  // Pagination (stage 8+)
  const PAGE_SIZE = 50;
  const [page, setPage] = React.useState(1);
  React.useEffect(() => { setPage(1); }, [filterYear, filterMonthFrom, filterMonthTo]);
  const paged = stage >= 8 ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered.slice(0, 100);
  const totalPages = stage >= 8 ? Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)) : 1;

  // Filters block (stage 3+) — native <select> until stage 7
  const filtersNative = (
    <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-end", flexWrap: "wrap" }} data-testid="pq-stage-filters-native">
      <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "#64748b" }}>
        Anno
        <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, marginTop: 4 }}>
          <option value="all">Tutti</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
          <option value="2026">2026</option>
        </select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "#64748b" }}>
        Mese da
        <select value={filterMonthFrom} onChange={(e) => setFilterMonthFrom(e.target.value)} disabled={filterYear === "all"} style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, marginTop: 4 }}>
          <option value="all">Tutti i mesi</option>
          {MONTHS_IT_LOCAL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label style={{ display: "flex", flexDirection: "column", fontSize: 11, color: "#64748b" }}>
        Mese a
        <select value={filterMonthTo} onChange={(e) => setFilterMonthTo(e.target.value)} disabled={filterYear === "all" || filterMonthFrom === "all"} style={{ padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 4, marginTop: 4 }}>
          <option value="all">—</option>
          {MONTHS_IT_LOCAL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    </div>
  );

  // Filters block — Radix Select (stage 7+)
  const filtersRadix = (
    <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "flex-end", flexWrap: "wrap" }} data-testid="pq-stage-filters-radix">
      <div style={{ minWidth: 110 }}>
        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="h-9" data-testid="pq-stage-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="2024">2024</SelectItem>
            <SelectItem value="2025">2025</SelectItem>
            <SelectItem value="2026">2026</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div style={{ minWidth: 140 }}>
        <Select value={filterMonthFrom} onValueChange={setFilterMonthFrom} disabled={filterYear === "all"}>
          <SelectTrigger className="h-9" data-testid="pq-stage-monthfrom"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {MONTHS_IT_LOCAL.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div style={{ minWidth: 140 }}>
        <Select value={filterMonthTo} onValueChange={setFilterMonthTo} disabled={filterYear === "all" || filterMonthFrom === "all"}>
          <SelectTrigger className="h-9" data-testid="pq-stage-monthto"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">—</SelectItem>
            {MONTHS_IT_LOCAL.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const header = (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600 }}>Lavoro Quotidiano — STAGE {stage}</h2>
      <p style={{ fontSize: 12, color: "#64748b" }}>
        {stage >= 2
          ? (loading ? "Caricamento…" : `${filtered.length} lavori (di ${items.length} totali)`)
          : "Solo header statico"}
      </p>
    </div>
  );

  // Plain list (stage 4) — no <table>
  const plainList = (
    <ul data-testid="pq-stage-plain-list" style={{ listStyle: "none", padding: 0, margin: 0 }}>
      {paged.length === 0 && <li style={{ padding: 8, color: "#94a3b8" }}>Nessun lavoro.</li>}
      {paged.map(p => (
        <li key={p.id} style={{ padding: "6px 8px", borderBottom: "1px solid #e2e8f0", fontSize: 13 }}>
          <strong>{p.clientName || "—"}</strong> · {p.expectedDueDate || "—"} · {p.status === "chiusa" ? "Chiusa" : "In corso"}
        </li>
      ))}
    </ul>
  );

  // Native HTML table (stage 5+)
  const nativeTable = (
    <table data-testid="pq-stage-native-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr style={{ background: "#f1f5f9" }}>
          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Cliente</th>
          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Data lavoro</th>
          <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #cbd5e1" }}>Stato</th>
        </tr>
      </thead>
      <tbody>
        {paged.length === 0 && <tr><td colSpan={3} style={{ padding: 12, textAlign: "center", color: "#94a3b8" }}>Nessun lavoro.</td></tr>}
        {paged.map(p => (
          <tr key={p.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
            <td style={{ padding: "6px 8px" }}>{p.clientName || "—"}</td>
            <td style={{ padding: "6px 8px" }}>{p.expectedDueDate || "—"}</td>
            <td style={{ padding: "6px 8px" }}>{p.status === "chiusa" ? "Chiusa" : "In corso"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const paginationBar = stage >= 8 ? (
    <div style={{ display: "flex", gap: 6, padding: "8px 0", fontSize: 12 }}>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ padding: "4px 8px", border: "1px solid #cbd5e1" }}>« Prec.</button>
      <span style={{ padding: "4px 8px" }}>Pagina {page} di {totalPages} ({filtered.length} totali)</span>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: "4px 8px", border: "1px solid #cbd5e1" }}>Succ. »</button>
    </div>
  ) : null;

  const content = (
    <div data-testid="lavoro-quotidiano-tab">
      <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", padding: 8, borderRadius: 4, marginBottom: 12, fontSize: 13 }}>
        <strong>STAGE {stage} ATTIVO</strong> — versione progressiva.
        Rimuovi <code>?stage=N</code> dall'URL per tornare alla versione completa.
      </div>
      {header}
      {stage >= 7 ? filtersRadix : (stage >= 3 ? filtersNative : null)}
      {stage >= 5 ? nativeTable : (stage >= 4 ? plainList : null)}
      {paginationBar}
    </div>
  );

  if (stage >= 6) {
    return (
      <Card className="bg-white border-teal-200/70 shadow-sm" data-testid="pq-active-section">
        <CardContent>{content}</CardContent>
      </Card>
    );
  }
  return content;
}

function PraticheQuotidianeTabFull({
  clients = [],
  operators = [],
  currentOperator,
  operatorRole,
  // Optional plumbing identical to RubricaClientiTab so we can open the
  // GLOBAL ClientDialog from inside the operational form when the typed
  // name does not exist in the central registry. After save we auto-select
  // the new client via the `pendingPraticaForm` effect below.
  onOpenNewClient,
  isClientDialogOpen,
  setClientForm,
  resetClientForm,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm(currentOperator));

  // Filters — allineati a "Diario Giornaliero": testo libero, periodo
  // con preset rapidi, gestione, area, operatore (creator), cliente.
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterOperator, setFilterOperator] = useState("");
  const [filterManagement, setFilterManagement] = useState("");
  const [filterArea, setFilterArea] = useState("");

  // Filtri Anno / Mese (refinement 22/02/2026) — pensati per la
  // revisione del lavoro svolto prima della fatturazione periodica.
  // Default: anno corrente, mesi "Tutti i mesi". Range "Mese da → Mese a"
  // supportato (es. Gen → Mar = 01/01 → 31/03 dell'anno selezionato).
  const [filterYear, setFilterYear] = useState(() => String(new Date().getFullYear()));
  const [filterMonthFrom, setFilterMonthFrom] = useState("all");
  const [filterMonthTo, setFilterMonthTo] = useState("all");

  // Sorting (default: nearest deadline first).
  const [sortKey, setSortKey] = useState("expectedDueDate");
  const [sortDir, setSortDir] = useState("asc");

  // Expanded row → history panel.
  const [expandedId, setExpandedId] = useState(null);

  // Stable callbacks per PracticeTable.memo — evitano busting del
  // memo a ogni render del genitore (22/02/2026, fix `insertBefore`).
  const handleToggleExpand = useCallback(
    (id) => setExpandedId((cur) => (cur === id ? null : id)),
    []
  );

  // Quick-note dialog state.
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState("");

  // Stable callback per PracticeTable.memo (22/02/2026).
  const handleNoteOpen = useCallback((p) => {
    setNoteTarget(p);
    setNoteText("");
  }, []);

  // Closure dialog state.
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeDate, setCloseDate] = useState("");
  const [closeTime, setCloseTime] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  const isAdmin = operatorRole === "amministratore" || operatorRole === "responsabile";

  // ─── Data loading (full reload only on mount; mutations update local state) ───
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listDailyPractices();
      setItems(data || []);
    } catch (e) {
      toast.error(`Errore caricamento: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Local-state mutation helpers (richiesta #8 — no full reload).
  const replaceItem = useCallback((updated) => {
    setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }, []);
  const insertItem = useCallback((created) => {
    setItems((prev) => [created, ...prev]);
  }, []);
  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // ─── Form helpers ───
  // Selettore cliente collegato al REGISTRO CENTRALIZZATO (`clients` prop
  // popolato da App.js via GET /api/clients). Nessun mirror locale: id e
  // fullName sono gli stessi usati da Pratiche, Fatture, Scadenze.
  const handleClientChange = (cid, fullName) => {
    setForm((f) => ({
      ...f,
      clientId: cid,
      clientName: fullName || (clients.find((x) => x.id === cid)?.fullName || ""),
    }));
  };

  // Inline-creation: l'operatore digita un nome che non esiste nel registro
  // → apriamo il GLOBAL ClientDialog (lo stesso usato da Cliente/Pratica/
  // Fattura) con il nome pre-fillato. Stash della form corrente per
  // riaprirla con il neonato cliente auto-selezionato dopo il save.
  const [pendingPraticaForm, setPendingPraticaForm] = useState(null);
  const handleCreateNewClientFromPratica = (typedName) => {
    if (!onOpenNewClient) return;
    setPendingPraticaForm({ ...form, _typedName: typedName });
    setDialogOpen(false);
    if (resetClientForm) resetClientForm();
    if (setClientForm) setClientForm((prev) => ({ ...(prev || {}), fullName: typedName }));
    onOpenNewClient();
  };
  useEffect(() => {
    if (!pendingPraticaForm) return;
    if (isClientDialogOpen) return; // wait until the ClientDialog closes
    const typed = (pendingPraticaForm._typedName || "").toLowerCase();
    const found = clients.find((c) => (c.fullName || "").toLowerCase() === typed);
    setForm({
      ...pendingPraticaForm,
      clientId: found?.id || pendingPraticaForm.clientId || "",
      clientName: found?.fullName || pendingPraticaForm.clientName || "",
    });
    setPendingPraticaForm(null);
    setDialogOpen(true);
  }, [isClientDialogOpen, pendingPraticaForm, clients]);
  const handleManagementChange = (mt) => {
    setForm((f) => ({
      ...f,
      managementType: normalizeManagementType(mt),
    }));
  };
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(currentOperator));
    setDialogOpen(true);
  };

  // Quick-action shortcut: il bottone "Lavoro Quotidiano" in App.js
  // dispatcha `lavoro:open-new` dopo aver attivato il sub-tab. L'effect
  // intercetta l'evento e apre direttamente il dialog di creazione, così
  // un click diventa un flow di produttività immediato (no extra click).
  useEffect(() => {
    const onOpenNew = () => openCreate();
    window.addEventListener("lavoro:open-new", onOpenNew);
    return () => window.removeEventListener("lavoro:open-new", onOpenNew);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOperator]);
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      clientId: p.clientId || "",
      clientName: p.clientName || "",
      managementType: p.managementType || "agenzia",
      operationalArea: p.operationalArea || "Lavoro",
      creatorOperatorId: p.creatorOperatorId || "",
      creatorOperatorName: p.creatorOperatorName || "",
      // "Data lavoro" — fallback a oggi se record legacy senza data.
      expectedDueDate: p.expectedDueDate || todayISO(),
      // Orario opzionale aggiunto il 19/02/2026. Legacy senza orario
      // → stringa vuota (mostra placeholder nel campo time).
      expectedDueTime: p.expectedDueTime || "",
      description: p.description || "",
      expenses: {
        amount: p.expenses?.amount ?? 0,
        paid: !!p.expenses?.paid,
        description: p.expenses?.description || "",
        paymentMethod: p.expenses?.paymentMethod || "",
      },
      status: p.status || "aperta",
      // Edit form: legacy records lacking `isOpen` are treated as
      // "open lifecycle" (status drives the UI). The checkbox stays
      // in sync with the current status so toggling is intuitive.
      isOpen: p.isOpen === undefined ? (p.status !== "chiusa") : !!p.isOpen,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.clientId) { toast.error("Seleziona un cliente"); return; }
    if (!form.description?.trim()) { toast.error("Descrizione attività obbligatoria"); return; }
    try {
      if (editingId) {
        const upd = await api.updateDailyPractice(editingId, form);
        replaceItem(upd);                // local state update only
        toast.success("Lavoro aggiornato");
      } else {
        const created = await api.createDailyPractice(form);
        insertItem(created);             // local state update only
        toast.success("Lavoro creato");
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(`Errore salvataggio: ${e.message || e}`);
    }
  };

  // ── Quick note: ENTER-to-save + local update (richiesta #2) ──
  const handleQuickNote = async () => {
    if (!noteTarget || !noteText.trim()) return;
    try {
      const upd = await api.addDailyPracticeNote(noteTarget.id, noteText.trim());
      replaceItem(upd);                  // no full reload
      toast.success("Nota aggiunta");
      setNoteTarget(null);
      setNoteText("");
    } catch (e) {
      toast.error(`Errore nota: ${e.message || e}`);
    }
  };
  const onNoteKeyDown = (e) => {
    // ENTER without Shift saves; Shift+ENTER inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleQuickNote();
    }
  };

  // ── Closure flow ──
  // NOTE (18/02/2026): semantic refinement — il setter `setCloseJustified`
  // è stato rimosso insieme alla checkbox "Ritardo giustificato" (no più
  // overdue semantics). Il dialog gestisce SOLO data + ora + note.
  const openCloseDialog = (p) => {
    const now = new Date();
    setCloseTarget(p);
    setCloseDate(now.toISOString().slice(0, 10));
    setCloseTime(now.toTimeString().slice(0, 5));
    setCloseNotes("");
  };
  const handleClose = async () => {
    if (!closeTarget) return;
    try {
      const closedAt = new Date(`${closeDate}T${closeTime}`).toISOString();
      const upd = await api.updateDailyPractice(closeTarget.id, {
        ...closeTarget,
        status: "chiusa",
        closedAt,
        closureNotes: closeNotes || "",
      });
      replaceItem(upd);
      toast.success("Lavoro chiuso");
      setCloseTarget(null);
    } catch (e) {
      toast.error(`Errore archiviazione: ${e.message || e}`);
    }
  };

  // ─── Filters + sort ───
  // Perf: memoize the derived lists so they are recomputed only when an
  // actual dependency changes.
  const filterOperatorOptions = useMemo(
    () => operators.map((o) => ({ value: o.id, label: o.fullName || o.name })),
    [operators]
  );
  // Elenco clienti UNIVOCI presenti nei lavori — coerente con la lista
  // `contactClients` di Diario (filtro popolato dal dataset, non dal
  // registro globale).
  const filterClientOptions = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      if (p.clientId && p.clientName && !map.has(p.clientId)) {
        map.set(p.clientId, { value: p.clientId, label: p.clientName });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "it"));
  }, [items]);

  // Calcolo range date per il filtro "Periodo" (stessa logica del Diario).
  const periodRange = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const iso = (d) => d.toISOString().slice(0, 10);
    switch (filterPeriod) {
      case "oggi":      return { from: iso(today), to: iso(today) };
      case "ieri": {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        return { from: iso(y), to: iso(y) };
      }
      case "7giorni": {
        const s = new Date(today); s.setDate(s.getDate() - 6);
        return { from: iso(s), to: iso(today) };
      }
      case "15giorni": {
        const s = new Date(today); s.setDate(s.getDate() - 14);
        return { from: iso(s), to: iso(today) };
      }
      case "mese": {
        const s = new Date(today); s.setDate(s.getDate() - 29);
        return { from: iso(s), to: iso(today) };
      }
      case "custom":   return { from: filterFrom || "", to: filterTo || "" };
      default:         return { from: "", to: "" };
    }
  }, [filterPeriod, filterFrom, filterTo]);

  // Range Anno/Mese (refinement 22/02/2026). Indipendente da
  // `periodRange` ma additivo: la riga deve passare ENTRAMBI per
  // essere visibile (intersezione). Quando l'anno è "all" il filtro
  // è disattivato.
  const yearMonthRange = useMemo(() => {
    if (!filterYear || filterYear === "all") return { from: "", to: "" };
    const y = filterYear;
    const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
    const mToRaw = filterMonthTo !== "all" ? filterMonthTo : null;
    // Se ho solo "Mese da" senza "Mese a" → singolo mese.
    // Se ho entrambi e da > a → li scambio (più user-friendly).
    let m1 = mFrom;
    let m2 = mToRaw || mFrom;
    if (m1 && m2 && m1 > m2) { const t = m1; m1 = m2; m2 = t; }
    const from = m1 ? `${y}-${m1}-01` : `${y}-01-01`;
    const to = m2 ? `${y}-${m2}-${lastDayOfMonth(y, m2)}` : `${y}-12-31`;
    return { from, to };
  }, [filterYear, filterMonthFrom, filterMonthTo]);

  const passesFilters = useCallback(
    (p) => {
      // Filtro "Operatore" è sul CREATORE del lavoro (registro operativo).
      if (filterOperator && p.creatorOperatorId !== filterOperator) return false;
      if (filterManagement && p.managementType !== filterManagement) return false;
      if (filterArea && p.operationalArea !== filterArea) return false;
      if (filterClient && p.clientId !== filterClient) return false;
      // Periodo: confronto su `expectedDueDate` (= Data lavoro).
      if (periodRange.from && (p.expectedDueDate || "") < periodRange.from) return false;
      if (periodRange.to   && (p.expectedDueDate || "") > periodRange.to)   return false;
      // Anno/Mese (refinement 22/02/2026): stesso campo `expectedDueDate`.
      if (yearMonthRange.from && (p.expectedDueDate || "") < yearMonthRange.from) return false;
      if (yearMonthRange.to   && (p.expectedDueDate || "") > yearMonthRange.to)   return false;
      // Ricerca testo libero su cliente + descrizione + area.
      const q = searchQuery.trim().toLowerCase();
      if (q) {
        const hay = `${p.clientName || ""} ${p.description || ""} ${p.operationalArea || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    },
    [filterOperator, filterManagement, filterArea, filterClient, periodRange, yearMonthRange, searchQuery]
  );

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    filterPeriod !== "all" ||
    filterOperator ||
    filterManagement ||
    filterArea ||
    filterClient ||
    filterYear !== "all" ||
    filterMonthFrom !== "all" ||
    filterMonthTo !== "all"
  );
  const resetFilters = () => {
    setSearchQuery("");
    setFilterPeriod("all");
    setFilterFrom("");
    setFilterTo("");
    setFilterOperator("");
    setFilterManagement("");
    setFilterArea("");
    setFilterClient("");
    setFilterYear(String(new Date().getFullYear()));
    setFilterMonthFrom("all");
    setFilterMonthTo("all");
  };
  // Anni disponibili: estraggo dagli `items` (data-driven) + anno
  // corrente. Coerente con la fatturazione periodica che lavora su
  // anni reali presenti nei dati.
  const filterYearOptions = useMemo(() => {
    const set = new Set();
    const cy = new Date().getFullYear();
    set.add(String(cy));
    items.forEach((p) => {
      const d = p.expectedDueDate;
      if (d && /^\d{4}-/.test(d)) set.add(d.slice(0, 4));
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  }, [items]);

  const sortRows = useCallback(
    (rows) => {
      const cmp = COMPARATORS[sortKey] || COMPARATORS.expectedDueDate;
      const sorted = [...rows].sort(cmp);
      return sortDir === "asc" ? sorted : sorted.reverse();
    },
    [sortKey, sortDir]
  );
  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const visible = useMemo(
    () => items.filter(passesFilters),
    [items, passesFilters]
  );
  // Lista unificata (refinement UI 22/02/2026): tutti i record (aperti +
  // conclusi) appaiono in un'unica tabella ordinata. Lo stato "Concluso"
  // resta come flag sul singolo record per la futura revisione
  // amministrativa / fatturazione periodica, ma non viene più spostato
  // in una sezione archivio separata.
  const allRows = useMemo(
    () => sortRows(visible),
    [visible, sortRows]
  );
  const open = useMemo(
    () => visible.filter((p) => p.status !== "chiusa"),
    [visible]
  );
  const closed = useMemo(
    () => visible.filter((p) => p.status === "chiusa"),
    [visible]
  );

  // Paginazione globale unificata (refinement UI 22/02/2026).
  const {
    paged: pagedRows,
    paginationBar: rowsPaginationBar,
  } = usePagination(allRows, {
    storageKey: "lavoro-quotidiano-tutti",
    itemLabelSingular: "lavoro",
    itemLabelPlural: "lavori",
  });

  // ─── Export / Print ─── (allineati a Diario Giornaliero)
  // Costruisce una riga flat per ogni voce attualmente visibile (filtered+
  // sorted, sia aperti che chiusi) per export CSV e stampa.
  const buildExportRows = () => {
    const all = [...open, ...closed];
    return all.map((p) => ({
      Data: p.expectedDueDate
        ? new Date(p.expectedDueDate + "T00:00:00").toLocaleDateString("it-IT")
        : "",
      Cliente: p.clientName || "",
      Gestione: p.managementType === "studio" ? "Studio" : "Agenzia",
      Area: p.operationalArea || "",
      Operatore: p.creatorOperatorName || "",
      Stato: p.status === "chiusa" ? "Chiusa" : "In corso",
      Descrizione: (p.description || "").replace(/[\r\n]+/g, " ").trim(),
      Spese: p.expenses?.amount ? Number(p.expenses.amount).toFixed(2) : "",
      Pagate: p.expenses?.paid ? "Sì" : (p.expenses?.amount ? "No" : ""),
    }));
  };
  // Riepilogo dei filtri attivi: usato come sotto-titolo nei report
  // stampati/esportati così l'operatore sa esattamente cosa contiene il
  // file (req: "include active filters in the print/export header").
  const buildFilterSummary = () => {
    const parts = [];
    if (searchQuery.trim()) parts.push(`Cerca: "${searchQuery.trim()}"`);
    if (filterPeriod !== "all") {
      const labels = { oggi: "Oggi", ieri: "Ieri", "7giorni": "Ultimi 7gg", "15giorni": "Ultimi 15gg", mese: "Ultimo mese", custom: "Personalizzato" };
      let s = `Periodo: ${labels[filterPeriod] || filterPeriod}`;
      if (filterPeriod === "custom" && (filterFrom || filterTo)) s += ` (${filterFrom || "…"} → ${filterTo || "…"})`;
      parts.push(s);
    }
    if (filterClient) {
      const c = filterClientOptions.find((o) => o.value === filterClient);
      if (c) parts.push(`Cliente: ${c.label}`);
    }
    if (filterOperator) {
      const o = filterOperatorOptions.find((x) => x.value === filterOperator);
      if (o) parts.push(`Operatore: ${o.label}`);
    }
    if (filterManagement) parts.push(`Gestione: ${filterManagement === "studio" ? "Studio" : "Agenzia"}`);
    if (filterArea) parts.push(`Area: ${filterArea}`);
    // Anno / Mese (refinement 22/02/2026).
    if (filterYear && filterYear !== "all") {
      let s = `Anno: ${filterYear}`;
      const mFrom = filterMonthFrom !== "all" ? filterMonthFrom : null;
      const mTo = filterMonthTo !== "all" ? filterMonthTo : null;
      if (mFrom && mTo && mFrom !== mTo) s += ` · Mesi: ${monthLabel(mFrom)} → ${monthLabel(mTo)}`;
      else if (mFrom || mTo) s += ` · Mese: ${monthLabel(mFrom || mTo)}`;
      parts.push(s);
    }
    return parts.join(" · ");
  };

  const handleExportExcel = () => {
    try {
      const rows = buildExportRows();
      if (rows.length === 0) {
        toast.info("Nessun lavoro da esportare");
        return;
      }
      const headers = Object.keys(rows[0]);
      const escape = (v) => {
        const s = String(v ?? "");
        return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        headers.join(";"),
        ...rows.map((r) => headers.map((h) => escape(r[h])).join(";")),
      ].join("\r\n");
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lavoro_quotidiano_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Esportati ${rows.length} lavori`);
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    }
  };

  const handlePrint = () => {
    const rows = buildExportRows();
    const w = window.open("", "_blank", "width=1100,height=800");
    if (!w) {
      toast.error("Apertura finestra di stampa bloccata dal browser");
      return;
    }
    const todayStr = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const filterLine = buildFilterSummary();
    const headers = ["Data", "Cliente", "Gestione", "Area", "Operatore", "Stato", "Descrizione", "Spese"];
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]));
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Lavoro Quotidiano</title>
      <style>
        *{box-sizing:border-box} body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;padding:24px;font-size:11px}
        h1{margin:0 0 4px;font-size:18px} .meta{color:#64748b;font-size:11px;margin-bottom:4px}
        .filters{color:#475569;font-size:11px;margin-bottom:16px;font-style:italic}
        table{width:100%;border-collapse:collapse;font-size:10.5px}
        th{text-align:left;background:#f1f5f9;color:#0f172a;font-weight:600;border-bottom:2px solid #cbd5e1;padding:6px 8px}
        td{padding:5px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top}
        tr:nth-child(even) td{background:#fafafa}
        .desc{max-width:380px;word-wrap:break-word}
        @media print{ body{padding:8mm} button{display:none} }
      </style></head><body>
      <h1>Lavoro Quotidiano</h1>
      <div class="meta">Stampato il ${todayStr} · ${rows.length} lavori</div>
      ${filterLine ? `<div class="filters">Filtri attivi: ${esc(filterLine)}</div>` : ""}
      <table>
        <thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${esc(r.Data)}</td>
            <td>${esc(r.Cliente)}</td>
            <td>${esc(r.Gestione)}</td>
            <td>${esc(r.Area)}</td>
            <td>${esc(r.Operatore)}</td>
            <td>${esc(r.Stato)}</td>
            <td class="desc">${esc(r.Descrizione)}</td>
            <td>${esc(r.Spese)}${r.Pagate ? ` (${esc(r.Pagate)})` : ""}</td>
          </tr>`).join("")}
        </tbody>
      </table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  /**
   * Stampa di una SINGOLA voce Lavoro Quotidiano (20/02/2026).
   *
   * Differente da `handlePrint` (che stampa la lista filtrata):
   * apre una scheda one-page A4 con tutti i campi della voce
   * scelta, comoda da archiviare in cartaceo o da inviare al
   * cliente come ricevuta del lavoro svolto. Riusa lo stesso
   * pattern HTML+CSS della stampa lista (no nuove dipendenze),
   * con layout a "label: valore" su due colonne.
   */
  const handlePrintSingle = (p) => {
    if (!p) return;
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) {
      toast.error("Apertura finestra di stampa bloccata dal browser");
      return;
    }
    const esc = (s) => String(s ?? "").replace(/[<>&]/g, (ch) => ({ "<":"&lt;",">":"&gt;","&":"&amp;" }[ch]));
    const todayStr = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const dueDate = p.expectedDueDate
      ? new Date(p.expectedDueDate + "T00:00:00").toLocaleDateString("it-IT")
      : "—";
    const dueTime = p.expectedDueTime || "";
    const expensesAmount = p.expenses?.amount ? `€ ${Number(p.expenses.amount).toFixed(2)}` : "—";
    const expensesPaid = p.expenses?.paid ? "Sì" : (p.expenses?.amount ? "No" : "—");
    const expensesPayment = p.expenses?.paymentMethod || "—";
    const expensesDesc = p.expenses?.description || "";
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/>
      <title>Lavoro — ${esc(p.clientName || "")}</title>
      <style>
        *{box-sizing:border-box}
        body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#1e293b;padding:28px;font-size:12px;max-width:780px;margin:0 auto}
        h1{margin:0 0 4px;font-size:20px;color:#0f172a}
        .meta{color:#64748b;font-size:11px;margin-bottom:16px}
        .badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10.5px;font-weight:600;margin-left:6px}
        .b-studio{background:#dbeafe;color:#1e3a8a}
        .b-agenzia{background:#fed7aa;color:#9a3412}
        .b-aperto{background:#fef9c3;color:#854d0e}
        .b-chiuso{background:#dcfce7;color:#166534}
        table.fields{width:100%;border-collapse:collapse;margin-top:8px}
        table.fields td{padding:7px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top;font-size:12px}
        table.fields td.label{width:35%;color:#475569;font-weight:600;background:#f8fafc}
        .section-title{margin:18px 0 6px;font-size:13px;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:.04em;border-bottom:2px solid #cbd5e1;padding-bottom:4px}
        .longtext{white-space:pre-wrap;background:#fafafa;border:1px solid #e2e8f0;padding:10px 12px;border-radius:6px;font-size:12px;line-height:1.5}
        .footer{margin-top:24px;color:#94a3b8;font-size:10px;border-top:1px solid #e2e8f0;padding-top:8px}
        @media print{ body{padding:8mm;max-width:none} button{display:none} }
      </style></head><body>
      <h1>Scheda Lavoro Quotidiano
        <span class="badge ${p.managementType === "studio" ? "b-studio" : "b-agenzia"}">${p.managementType === "studio" ? "Studio" : "Agenzia"}</span>
        <span class="badge ${p.status === "chiusa" ? "b-chiuso" : "b-aperto"}">${p.status === "chiusa" ? "Chiusa" : "In corso"}</span>
      </h1>
      <div class="meta">Stampato il ${todayStr} · ID ${esc(p.id || "—")}</div>

      <div class="section-title">Dati principali</div>
      <table class="fields">
        <tr><td class="label">Cliente</td><td>${esc(p.clientName || "—")}</td></tr>
        <tr><td class="label">Data lavoro</td><td>${esc(dueDate)}${dueTime ? ` · ${esc(dueTime)}` : ""}</td></tr>
        <tr><td class="label">Area operativa</td><td>${esc(p.operationalArea || "—")}</td></tr>
        <tr><td class="label">Operatore</td><td>${esc(p.creatorOperatorName || "—")}</td></tr>
        ${p.assignedOperatorName ? `<tr><td class="label">Co-operatori</td><td>${esc(p.assignedOperatorName)}</td></tr>` : ""}
      </table>

      <div class="section-title">Descrizione attività svolta</div>
      <div class="longtext">${esc(p.description || "(nessuna descrizione)")}</div>

      ${(p.expenses?.amount || p.expenses?.description) ? `
        <div class="section-title">Spese / Bolli</div>
        <table class="fields">
          <tr><td class="label">Importo</td><td>${esc(expensesAmount)}</td></tr>
          <tr><td class="label">Pagate</td><td>${esc(expensesPaid)}</td></tr>
          ${p.expenses?.paid ? `<tr><td class="label">Modalità pagamento</td><td>${esc(expensesPayment)}</td></tr>` : ""}
          ${expensesDesc ? `<tr><td class="label">Note spese</td><td>${esc(expensesDesc)}</td></tr>` : ""}
        </table>
      ` : ""}

      ${p.status === "chiusa" ? `
        <div class="section-title">Chiusura</div>
        <table class="fields">
          ${p.closedAt ? `<tr><td class="label">Chiusa il</td><td>${esc(new Date(p.closedAt).toLocaleString("it-IT"))}</td></tr>` : ""}
          ${p.closedByOperatorName ? `<tr><td class="label">Chiusa da</td><td>${esc(p.closedByOperatorName)}</td></tr>` : ""}
          ${p.closureNotes ? `<tr><td class="label">Note finali</td><td>${esc(p.closureNotes)}</td></tr>` : ""}
        </table>
      ` : ""}

      <div class="footer">Gestionale Gruppo Impresa · Scheda generata automaticamente</div>
      <script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>
      </body></html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div
      className="space-y-5 -mx-2 px-3 py-4 rounded-xl bg-gradient-to-br from-teal-50/60 via-white to-slate-50/50 ring-1 ring-teal-100/60"
      data-testid="lavoro-quotidiano-tab"
    >
      {/* ─── Header — identità visiva "operational workspace" (pastel teal).
           Pannello dedicato, leggermente più presente degli altri tab per
           segnalare che è l'area di lavoro operativo quotidiano. ─── */}
      <div className="flex items-center justify-between flex-wrap gap-3 pb-3 border-b border-teal-100">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-teal-100/80 flex items-center justify-center shrink-0">
            <ClipboardList className="h-5 w-5 text-teal-700" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800 leading-tight">
              Lavoro Quotidiano
              <span className="ml-2 text-[10px] uppercase tracking-wider text-teal-700 bg-teal-100/70 px-2 py-0.5 rounded-full align-middle">
                Workspace
              </span>
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {open.length} {open.length === 1 ? "aperto" : "aperti"}
              {closed.length > 0 ? ` · ${closed.length} ${closed.length === 1 ? "chiuso" : "chiusi"}` : ""}
              {hasActiveFilters ? " (filtrati)" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            data-testid="pq-print-btn"
            title={`Stampa ${open.length + closed.length} righe filtrate`}
            className="border-teal-200 hover:bg-teal-50 hover:text-teal-800"
          >
            <Printer className="h-4 w-4 mr-1.5" /> Stampa
            <span className="ml-1 text-xs opacity-70">({open.length + closed.length})</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportExcel}
            data-testid="pq-export-excel-btn"
            title={`Esporta ${open.length + closed.length} righe filtrate`}
            className="border-teal-200 hover:bg-teal-50 hover:text-teal-800"
          >
            <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Esporta Excel
            <span className="ml-1 text-xs opacity-70">({open.length + closed.length})</span>
          </Button>
          <Button onClick={openCreate} data-testid="pq-new-button" className="bg-teal-700 hover:bg-teal-800 text-white shadow-sm">
            <Plus className="h-4 w-4 mr-1" /> Nuovo Lavoro
          </Button>
        </div>
      </div>

      {/* ─── Filters — container soft (rounded-lg, bg-bianco/cream).
           Visivamente separato dalle liste sottostanti grazie all'icona Filter. ─── */}
      <Card className="p-3 bg-white/80 border-teal-100 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Filter className="h-3.5 w-3.5 text-teal-600" />
          <span className="text-[11px] uppercase tracking-wider font-medium text-teal-700">
            Filtri
          </span>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {/* Ricerca */}
          <div className="flex-1 min-w-[200px] flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Cerca
            </label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Cliente, descrizione, area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
                data-testid="pq-search"
              />
            </div>
          </div>
          {/* Periodo */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Periodo
            </label>
            <Select value={filterPeriod} onValueChange={(v) => { setFilterPeriod(v); if (v !== "custom") { setFilterFrom(""); setFilterTo(""); } }}>
              <SelectTrigger className="w-[140px] h-9" data-testid="pq-period-filter">
                <CalendarDays className="h-3.5 w-3.5 mr-1.5 text-gray-400" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="oggi">Oggi</SelectItem>
                <SelectItem value="ieri">Ieri</SelectItem>
                <SelectItem value="7giorni">Ultimi 7gg</SelectItem>
                <SelectItem value="15giorni">Ultimi 15gg</SelectItem>
                <SelectItem value="mese">Ultimo mese</SelectItem>
                <SelectItem value="custom">Personalizzato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filterPeriod === "custom" && (
            <>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Dal</label>
                <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="pq-period-from" />
              </div>
              <div className="flex flex-col gap-0.5">
                <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">Al</label>
                <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-[130px] h-9 text-sm" data-testid="pq-period-to" />
              </div>
            </>
          )}
          {/* Anno (refinement 22/02/2026) — default = anno corrente.
               `startTransition` evita il race React 19 tra la chiusura
               del portal Radix Select e la riconciliazione della
               tabella filtrata (crash `insertBefore`). */}
          <div className="flex flex-col gap-0.5 min-w-[110px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Anno
            </label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-[110px] h-9" data-testid="pq-year-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {filterYearOptions.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Mese da (refinement 22/02/2026). */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Mese da
            </label>
            <Select value={filterMonthFrom} onValueChange={setFilterMonthFrom} disabled={filterYear === "all"}>
              <SelectTrigger className="w-[140px] h-9" data-testid="pq-month-from-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i mesi</SelectItem>
                {MONTHS_IT.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Mese a (refinement 22/02/2026) — opzionale; se vuoto e Mese
              da è selezionato, filtra sul singolo mese. */}
          <div className="flex flex-col gap-0.5 min-w-[140px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Mese a
            </label>
            <Select value={filterMonthTo} onValueChange={setFilterMonthTo} disabled={filterYear === "all" || filterMonthFrom === "all"}>
              <SelectTrigger className="w-[140px] h-9" data-testid="pq-month-to-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">—</SelectItem>
                {MONTHS_IT.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Cliente */}
          <div className="flex flex-col gap-0.5 min-w-[160px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Cliente
            </label>
            <LabeledFilterSelectInline value={filterClient} onChange={setFilterClient} options={filterClientOptions} testId="pq-filter-client" placeholder="Tutti i clienti" />
          </div>
          {/* Gestione */}
          <div className="flex flex-col gap-0.5 min-w-[130px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Gestione
            </label>
            <LabeledFilterSelectInline value={filterManagement} onChange={setFilterManagement} options={MGMT_OPTIONS} testId="pq-filter-management" placeholder="Tutte" />
          </div>
          {/* Area */}
          <div className="flex flex-col gap-0.5 min-w-[130px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Area
            </label>
            <LabeledFilterSelectInline value={filterArea} onChange={setFilterArea} options={AREA_OPTIONS} testId="pq-filter-area" placeholder="Tutte" />
          </div>
          {/* Operatore */}
          <div className="flex flex-col gap-0.5 min-w-[150px]">
            <label className="text-[10px] uppercase tracking-wide text-slate-500 font-medium px-0.5">
              Operatore
            </label>
            <LabeledFilterSelectInline value={filterOperator} onChange={setFilterOperator} options={filterOperatorOptions} testId="pq-filter-operator" placeholder="Tutti" />
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="h-9 text-gray-500" data-testid="pq-reset-filters">
              <Filter className="h-3.5 w-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>
      </Card>

      {/* ─── Unified list — pannello principale "lavoro quotidiano" (bg
           bianco luminoso + bordo teal soft). Mostra tutti i record
           (in corso + conclusi) in un'unica lista. Lo stato "Concluso"
           resta come flag sulla singola riga per la futura revisione
           amministrativa / fatturazione periodica. ─── */}
      <Card className="bg-white border-teal-200/70 shadow-sm" data-testid="pq-active-section">
        <CardHeader className="pb-2 bg-teal-50/40 border-b border-teal-100/70 rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2 text-teal-900">
            <span className="h-2 w-2 rounded-full bg-teal-500 animate-pulse" />
            Lavori ({allRows.length})
            {closed.length > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                · {open.length} in corso · {closed.length} {closed.length === 1 ? "concluso" : "conclusi"}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Render strutturalmente stabile (22/02/2026 fix
              `insertBefore`): non commutiamo più tra 3 branch
              `loading | empty | table+pagination` perché ogni
              transizione cambiava il tipo di nodo radice del
              CardContent e generava race React 19 durante un
              setState concorrente (cambio filtro / arrivo dati).
              Ora l'albero è SEMPRE lo stesso: messaggio loading +
              messaggio empty + table + pagination, governati da
              `hidden`. La table è memoizzata via React.memo,
              quindi il costo di mount aggiuntivo è zero quando non
              ci sono righe. */}
          <p
            className="text-sm text-slate-500"
            hidden={!loading}
            data-testid="pq-loading-msg"
          >
            Caricamento…
          </p>
          <p
            className="text-sm text-slate-500"
            hidden={loading || allRows.length !== 0}
            data-testid="pq-empty-msg"
          >
            Nessun lavoro presente.
          </p>
          <div hidden={loading || allRows.length === 0} data-testid="pq-table-wrapper">
            <PracticeTable
              rows={pagedRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onToggleSort={toggleSort}
              expandedId={expandedId}
              onToggleExpand={handleToggleExpand}
              onEdit={openEdit}
              onNote={handleNoteOpen}
              onClose={openCloseDialog}
              onPrint={handlePrintSingle}
            />
            {rowsPaginationBar}
          </div>
        </CardContent>
      </Card>

      {/* ─── Create / Edit dialog ─── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Modifica Lavoro" : "Nuovo Lavoro"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* 1 — Data lavoro e ora (in alto, default oggi/ora corrente).
                 Pattern coerente con Diario (contactDate + contactTime). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data lavoro *</Label>
                <Input
                  type="date"
                  value={form.expectedDueDate}
                  onChange={(e) => setForm((f) => ({ ...f, expectedDueDate: e.target.value }))}
                  data-testid="pq-form-due"
                />
              </div>
              <div>
                <Label>Ora</Label>
                <Input
                  type="time"
                  value={form.expectedDueTime || ""}
                  onChange={(e) => setForm((f) => ({ ...f, expectedDueTime: e.target.value }))}
                  data-testid="pq-form-due-time"
                />
              </div>
            </div>

            {/* 2 — Cliente (obbligatorio) — riga dedicata, largo intero. */}
            <div>
              <Label>Cliente *</Label>
              <ClientCombobox
                value={form.clientId}
                onChange={handleClientChange}
                clients={clients}
                onCreateNew={onOpenNewClient ? handleCreateNewClientFromPratica : undefined}
                testId="pq-form-client"
                placeholder="Digita per cercare cliente nel registro..."
              />
            </div>

            {/* 3 — Operatore Registrazione (auto-filled, read-only). */}
            <div>
              <Label>Operatore Registrazione</Label>
              <Input value={form.creatorOperatorName} disabled data-testid="pq-form-creator" />
            </div>

            {/* 3b — Co-operatori (correzione semantica 22/02/2026).
                Il modulo è un REGISTRO di lavori svolti, non un task
                manager con assignment workflow. Questo campo serve
                SOLO per indicare eventuali co-operatori coinvolti nel
                lavoro registrato (storico/audit). Niente notifiche di
                assegnazione, niente workflow. Campo opzionale. */}
            <div>
              <Label>Co-operatori (opzionale)</Label>
              <OperatorMultiSelect
                operators={operators}
                value={form.assignedOperatorIds && form.assignedOperatorIds.length
                  ? form.assignedOperatorIds
                  : (form.assignedOperatorId ? [form.assignedOperatorId] : [])}
                onChange={(list) => {
                  const first = list[0] || "";
                  const firstOp = (operators || []).find(o => o.id === first);
                  setForm((f) => ({
                    ...f,
                    assignedOperatorId: first,
                    assignedOperatorIds: list,
                    assignedOperatorName: firstOp?.fullName || "",
                  }));
                }}
                placeholder="Eventuali colleghi coinvolti…"
                dataTestid="pq-form-assigned"
              />
            </div>

            {/* 4 + 5 — Gestionale + Area Operativa, su 2 colonne. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Gestionale</Label>
                <Select value={form.managementType} onValueChange={handleManagementChange}>
                  <SelectTrigger data-testid="pq-form-management"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="agenzia">Agenzia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Area Operativa</Label>
                <Select value={form.operationalArea} onValueChange={(v) => setForm((f) => ({ ...f, operationalArea: v }))}>
                  <SelectTrigger data-testid="pq-form-area"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AREA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 6 — Descrizione attività (obbligatoria, enfatizzata). */}
            <div>
              <Label className="text-sm font-semibold text-slate-800">Descrizione attività *</Label>
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descrivi nel dettaglio il lavoro svolto..."
                className="border-slate-300 focus-visible:ring-blue-500"
                data-testid="pq-form-description"
              />
            </div>

            {/* Operational simplification (18/02/2026 + correzione
                semantica 22/02/2026). Il modulo è un REGISTRO DI
                LAVORI SVOLTI. Default: lavoro già concluso → archiviato.
                Se l'attività continua nei giorni successivi si crea
                un nuovo record per quel giorno; NON si "tiene aperta"
                la stessa entry. La checkbox resta per i casi in cui
                serve davvero seguire il lavoro nel tempo. */}
            <label
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 cursor-pointer hover:bg-slate-100/70 transition-colors"
              data-testid="pq-form-is-open-row"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 cursor-pointer accent-amber-600"
                checked={!!form.isOpen}
                onChange={(e) => setForm((f) => ({ ...f, isOpen: e.target.checked }))}
                data-testid="pq-form-is-open-checkbox"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">Lavoro ancora in corso</div>
                <div className="text-[11px] text-slate-500 leading-snug">
                  Lasciato vuoto, il lavoro è considerato già svolto e
                  archiviato. Spunta solo se vuoi tenerlo in elenco fra
                  i lavori in corso (potrai concluderlo in un secondo
                  momento).
                </div>
              </div>
            </label>

            {/* 7 — Bolli / Spese. Quando "Già pagato" è spuntato compare
                 il dropdown "Modalità di pagamento" (Contanti/POS/Bonifico). */}
            <div className="border rounded-md p-3 space-y-2">
              <p className="text-sm font-medium">Bolli / Spese</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Importo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.expenses.amount}
                    onChange={(e) => setForm((f) => ({ ...f, expenses: { ...f.expenses, amount: parseFloat(e.target.value) || 0 } }))}
                    data-testid="pq-form-expense-amount"
                  />
                </div>
                <div>
                  <Label className="text-xs">Descrizione</Label>
                  <Input
                    placeholder="es. marca da bollo, F24, racc."
                    value={form.expenses.description}
                    onChange={(e) => setForm((f) => ({ ...f, expenses: { ...f.expenses, description: e.target.value } }))}
                    data-testid="pq-form-expense-desc"
                  />
                </div>
                <label className="flex items-center gap-2 mt-5 cursor-pointer">
                  <Checkbox
                    checked={form.expenses.paid}
                    onCheckedChange={(v) => setForm((f) => ({
                      ...f,
                      expenses: {
                        ...f.expenses,
                        paid: v === true,
                        // Reset method quando "Già pagato" viene
                        // disattivato, così non resta valorizzato
                        // un dato inconsistente nel DB.
                        paymentMethod: v === true ? (f.expenses.paymentMethod || "") : "",
                      },
                    }))}
                    data-testid="pq-form-expense-paid"
                  />
                  <span className="text-sm">Già pagato</span>
                </label>
              </div>
              {form.expenses.paid && (
                <div data-testid="pq-form-payment-method-row">
                  <Label className="text-xs">Modalità di pagamento</Label>
                  <Select
                    value={form.expenses.paymentMethod || ""}
                    onValueChange={(v) => setForm((f) => ({
                      ...f,
                      expenses: { ...f.expenses, paymentMethod: v },
                    }))}
                  >
                    <SelectTrigger className="w-full sm:w-1/2" data-testid="pq-form-payment-method">
                      <SelectValue placeholder="Seleziona modalità..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contanti">Contanti</SelectItem>
                      <SelectItem value="POS">POS</SelectItem>
                      <SelectItem value="Bonifico">Bonifico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            {editingId && (() => {
              // "Stampa scheda" disponibile solo in modalità modifica
              // (per "Nuovo Lavoro" non c'è ancora nulla di salvato).
              // Ricostruiamo la pratica dalla lista (per i campi
              // server-side come closedAt/operatorName) MERGED con il
              // form corrente, così la stampa riflette anche le
              // modifiche non ancora salvate.
              const existing = items.find((it) => it.id === editingId) || {};
              const merged = { ...existing, ...form, id: editingId };
              return (
                <Button
                  variant="outline"
                  onClick={() => handlePrintSingle(merged)}
                  data-testid="pq-form-print"
                  className="text-slate-700"
                >
                  <Printer className="h-4 w-4 mr-1.5" /> Stampa scheda
                </Button>
              );
            })()}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} data-testid="pq-form-save">
              {editingId ? "Salva modifiche" : "Crea lavoro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Quick Note dialog (ENTER-to-save) ─── */}
      <Dialog open={!!noteTarget} onOpenChange={(o) => !o && setNoteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nota rapida — {noteTarget?.clientName}</DialogTitle>
          </DialogHeader>
          <Textarea
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={onNoteKeyDown}
            placeholder="Premi INVIO per salvare, SHIFT+INVIO per andare a capo"
            autoFocus
            data-testid="pq-note-textarea"
          />
          <p className="text-[11px] text-slate-500">
            Timestamp e operatore vengono aggiunti automaticamente.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteTarget(null)}>Annulla</Button>
            <Button onClick={handleQuickNote} data-testid="pq-note-save">Aggiungi nota</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Archive dialog — "Lavoro Quotidiano" è un registro
           operativo, NON un task manager (correzione semantica
           22/02/2026). Niente "chiudi/data chiusura/scadenza":
           qui registriamo che il lavoro è da considerare concluso
           per archiviarlo. La data di archiviazione è opzionale e
           ammorbidita rispetto al precedente concetto di "chiusura". */}
      <Dialog open={!!closeTarget} onOpenChange={(o) => !o && setCloseTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archivia lavoro — {closeTarget?.clientName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data archiviazione</Label>
                <Input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} data-testid="pq-close-date" />
              </div>
              <div>
                <Label>Orario</Label>
                <Input type="time" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} data-testid="pq-close-time" />
              </div>
            </div>
            <div>
              <Label>Note finali (opzionali)</Label>
              <Textarea
                rows={3}
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                data-testid="pq-close-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseTarget(null)}>Annulla</Button>
            <Button onClick={handleClose} data-testid="pq-close-confirm">Conferma chiusura</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Reusable list table ───
 * Memoizzato (22/02/2026 fix `insertBefore`/`removeChild`): il
 * PracticeTable contiene N <tbody> ed è il singolo punto in cui
 * React 19 può ri-allineare DOM nodes pesanti. Bloccando il
 * re-render quando le props non cambiano (rows, sortKey, sortDir,
 * expandedId, callbacks stabili) evitiamo che setState in
 * componenti genitori non collegati alla tabella scatenino
 * reconciliation della tabella mid-update di altri portali (Radix
 * Select dei filtri, ecc.).
 */
const PracticeTable = React.memo(function PracticeTable({
  rows, sortKey, sortDir, onToggleSort,
  expandedId, onToggleExpand,
  onEdit, onNote, onClose, onPrint,
}) {
  const SortHeader = ({ k, label }) => (
    <th
      className="py-2 pr-2 select-none cursor-pointer"
      onClick={() => onToggleSort(k)}
      data-testid={`pq-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k
          ? (sortDir === "asc"
              ? <ArrowUp className="h-3 w-3 text-blue-600" />
              : <ArrowDown className="h-3 w-3 text-blue-600" />)
          : <ArrowUpDown className="h-3 w-3 text-slate-400" />}
      </span>
    </th>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            <SortHeader k="clientName" label="Cliente" />
            <th className="py-2 pr-2">Area</th>
            <SortHeader k="creatorOperatorName" label="Operatore" />
            <SortHeader k="expectedDueDate" label="Data lavoro" />
            <SortHeader k="status" label="Stato" />
            <th className="py-2 pr-2">Spese</th>
            <th className="py-2 pr-2 text-right">Azioni</th>
          </tr>
        </thead>
        {/* Empty-state row — SEMPRE renderizzata in un <tbody> stabile
            con `hidden` toggled (sostituisce ternary che variava la
            struttura figli del <table> e generava reconciliation
            failures `removeChild` durante i cambi di filtro). */}
        <tbody key="__empty__" hidden={rows.length !== 0}>
          <tr>
            <td colSpan={7} className="py-6 text-center text-sm text-slate-400">
              Nessun lavoro corrisponde ai filtri.
            </td>
          </tr>
        </tbody>
        {rows.map((p) => {
          const expanded = expandedId === p.id;
          // ─── Multi-`<tbody>` pattern (22/02/2026) ───
          // Ogni row vive in un proprio <tbody> con key stabile invece di
          // un <React.Fragment key>: questo elimina la causa del crash
          // React 19 `insertBefore` quando il numero di <tr> per row
          // cambia (espansione cronologia + righe filtrate). La
          // riconciliazione del parent <table> opera solo sui <tbody>
          // siblings (1 per row, numero stabile).
          return (
            <tbody key={p.id}>
              <tr
                className={`border-b transition-colors ${rowVisualClass(p)}`}
                data-testid={`pq-row-${p.id}`}
              >
                <td className="py-2 pr-2 font-medium">
                  <button
                    type="button"
                    onClick={() => onToggleExpand(p.id)}
                    className="text-left hover:underline"
                    title="Mostra/Nascondi cronologia"
                  >
                    {p.clientName || "—"}
                  </button>
                </td>
                <td className="py-2 pr-2">
                  <Badge variant="secondary">{p.managementType === "studio" ? "Studio" : "Agenzia"}</Badge>
                  <span className="ml-1 text-xs text-slate-500">/ {getAreaLabel(p.operationalArea)}</span>
                </td>
                <td className="py-2 pr-2">{p.creatorOperatorName || <span className="text-slate-400">—</span>}</td>
                <td className="py-2 pr-2">
                  {p.expectedDueDate
                    ? <span>{p.expectedDueDate}</span>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="py-2 pr-2">
                  {p.status === "chiusa"
                    ? <Badge variant="outline" className="border-green-500 text-green-700">Chiusa</Badge>
                    : <Badge>In corso</Badge>}
                </td>
                <td className="py-2 pr-2 text-xs">
                  {p.expenses?.amount > 0 ? (
                    p.expenses.paid ? (
                      <span className="text-slate-600">
                        € {Number(p.expenses.amount).toFixed(2)}
                        <span className="text-green-700 ml-1">✓ pagato</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                        € {Number(p.expenses.amount).toFixed(2)} · da pagare
                      </span>
                    )
                  ) : "—"}
                </td>
                <td className="py-2 pr-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 px-2 gap-1.5" onClick={() => onNote(p)} data-testid={`pq-action-note-${p.id}`}>
                    <MessageSquare className="h-3.5 w-3.5" />
                    <span className="text-xs">Nota</span>
                  </Button>
                  {onClose && p.status !== "chiusa" && (
                    <Button size="sm" variant="ghost" className="h-7 px-2 gap-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" onClick={() => onClose(p)} data-testid={`pq-action-close-${p.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span className="text-xs">Chiudi</span>
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 px-2 gap-1.5" onClick={() => onPrint(p)} data-testid={`pq-action-print-${p.id}`}>
                    <Printer className="h-3.5 w-3.5" />
                    <span className="text-xs">Stampa</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 gap-1.5" onClick={() => onEdit(p)} data-testid={`pq-action-edit-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                    <span className="text-xs">Modifica</span>
                  </Button>
                </td>
              </tr>
              {/* Riga espansione SEMPRE renderizzata ma con `hidden`
                  attribute: evita la causa principale del crash
                  React 19 (numero di <tr> figli che cambia mid-render).
                  HTML5 supporta `<tr hidden>` correttamente. */}
              <tr hidden={!expanded} data-testid={`pq-history-row-${p.id}`}>
                <td colSpan={7} className="bg-slate-50 px-3 py-2 border-b">
                  {expanded && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Cronologia</p>
                        <HistoryPanel events={p.history} testId={`pq-history-${p.id}`} />
                      </div>
                      <div className="text-xs space-y-1">
                        {p.description && (
                          <div>
                            <span className="text-slate-500">Descrizione:</span>{" "}
                            <span className="text-slate-800">{p.description}</span>
                          </div>
                        )}
                        {p.closureNotes && (
                          <div>
                            <span className="text-slate-500">Note di conclusione:</span>{" "}
                            <span className="text-slate-800 whitespace-pre-line">{p.closureNotes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          );
        })}
      </table>
    </div>
  );
});
